import { ListItem, ListItemText, TextField, Checkbox } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { HealthcareUnitResponse } from 'requests/nswag/nll/NLLClient';

type HealthcareUnitProps = {
  organisationId: string;
  organisationName: string;
  onAddChangesToUnit: (location: string, hsaId: string, organisationId: string, name: string) => void;
  onRemovedUnit: (organisationId: string) => void;
  healthcareUnit: HealthcareUnitResponse | undefined;
};

const NLLHealthcareUnitListItem: React.FC<HealthcareUnitProps> = (props) => {
  const { organisationId, organisationName, onAddChangesToUnit, onRemovedUnit, healthcareUnit } = props;

  const [unitName, setUnitName] = useState<string | undefined>('');
  const [unitHsaId, setUnitHsaId] = useState<string | undefined>('');
  const [unitLocation, setUnitLocation] = useState<string | undefined>('');
  const [unitAdded, setUnitAdded] = useState<boolean>(healthcareUnit !== undefined);

  useEffect(() => {
    if (healthcareUnit) {
      setUnitName(healthcareUnit.name);
      setUnitHsaId(healthcareUnit.hsaId);
      setUnitLocation(healthcareUnit.location);
    }
    setUnitAdded(healthcareUnit !== undefined);
  }, [healthcareUnit]);

  const canApplyUnitName = unitName !== '' && unitName !== undefined;
  const canApplyUnitLocation = unitLocation !== '' && unitLocation !== undefined;
  const canApplyUnitHsaId = unitHsaId !== '' && unitHsaId !== undefined;

  const canApplyUnit = canApplyUnitName && canApplyUnitLocation && canApplyUnitHsaId;

  const removeUnit = () => {
    onRemovedUnit(organisationId);
    setUnitAdded(false);
  };

  const onCheckMarkClick = () => {
    if (canApplyUnit && !unitAdded) {
      onAddChangesToUnit(unitLocation, unitHsaId, organisationId, unitName);
      setUnitAdded(true);
      return;
    }
    removeUnit();
  };

  const onUnitNameChange = (updatedUnitName: string) => {
    setUnitName(updatedUnitName);
    if (unitAdded && updatedUnitName !== '' && canApplyUnitLocation && canApplyUnitHsaId)
      onAddChangesToUnit(unitLocation, unitHsaId, organisationId, updatedUnitName);
    else if (updatedUnitName === '') removeUnit();
  };

  const onUnitHsaIdChange = (updatedHsaId: string) => {
    setUnitHsaId(updatedHsaId);
    if (unitAdded && updatedHsaId !== '' && canApplyUnitName && canApplyUnitLocation)
      onAddChangesToUnit(unitLocation, updatedHsaId, organisationId, unitName);
    else if (updatedHsaId === '') removeUnit();
  };

  const onUnitLocationChange = (updatedLocationId: string) => {
    setUnitLocation(updatedLocationId);
    if (unitAdded && updatedLocationId !== '' && canApplyUnitName && canApplyUnitHsaId)
      onAddChangesToUnit(updatedLocationId, unitHsaId, organisationId, unitName);
    else if (updatedLocationId === '') removeUnit();
  };

  return (
    <ListItem>
      <Stack>
        <ListItemText primary={organisationName} />
        <Stack direction={'row'} spacing={'12px'} marginTop={'4px'}>
          <Checkbox disabled={!canApplyUnit} onClick={onCheckMarkClick} checked={unitAdded} />
          <TextField
            value={unitName}
            variant="outlined"
            label="NLL unit name"
            required
            onChange={(e) => onUnitNameChange(e.target.value)}
          />
          <TextField
            value={unitHsaId}
            variant="outlined"
            label="HsaId"
            required
            onChange={(e) => onUnitHsaIdChange(e.target.value)}
          />
          <TextField
            value={unitLocation}
            variant="outlined"
            label="Location"
            required
            onChange={(e) => onUnitLocationChange(e.target.value)}
          />
        </Stack>
      </Stack>
    </ListItem>
  );
};

export default NLLHealthcareUnitListItem;
